import {
  addDays,
  emojiRow,
  londonDateString,
  MAX_TOTAL,
  TIER_COLOR,
  tierForTotal,
  type PlayerStats,
} from '@geosport/shared';
import { useEffect } from 'react';
import { Shell } from '../components/Shell';
import { track } from '../lib/analytics';
import { Link } from '../lib/router';
import { useApi } from '../lib/useApi';

const DIST_BUCKETS = [
  [0, 199],
  [200, 399],
  [400, 599],
  [600, 799],
  [800, 1000],
] as const;

function prettyDate(date: string): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

/** Plain SVG bar chart of the last 7 days, coloured by tier (PRD §10, no chart library). */
function WeekChart({ results }: { results: PlayerStats['results'] }) {
  const today = londonDateString();
  const days = Array.from({ length: 7 }, (_, i) => addDays(today, i - 6));
  const byDate = new Map(results.map((r) => [r.date, r.total]));
  const w = 320;
  const h = 120;
  const barW = 32;
  const gap = (w - 7 * barW) / 6;
  return (
    <svg
      viewBox={`0 0 ${w} ${h + 24}`}
      className="w-full"
      role="img"
      aria-label="Scores for the last 7 days"
    >
      {days.map((d, i) => {
        const total = byDate.get(d);
        const x = i * (barW + gap);
        const barH = total === undefined ? 0 : Math.max(3, (total / MAX_TOTAL) * h);
        const label = new Date(`${d}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'narrow' });
        return (
          <g key={d}>
            <rect x={x} y={0} width={barW} height={h} rx={6} fill="rgba(255,255,255,0.06)" />
            {total !== undefined && (
              <rect
                x={x}
                y={h - barH}
                width={barW}
                height={barH}
                rx={6}
                fill={TIER_COLOR[tierForTotal(total)]}
              />
            )}
            <text
              x={x + barW / 2}
              y={h + 16}
              textAnchor="middle"
              fontSize="11"
              fill={d === today ? 'white' : 'rgba(255,255,255,0.6)'}
              fontWeight={d === today ? 700 : 400}
            >
              {label}
            </text>
            {total !== undefined && (
              <text
                x={x + barW / 2}
                y={h - barH - 4}
                textAnchor="middle"
                fontSize="10"
                fill="white"
              >
                {total}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function Distribution({ results }: { results: PlayerStats['results'] }) {
  const counts = DIST_BUCKETS.map(
    ([lo, hi]) => results.filter((r) => r.total >= lo && r.total <= hi).length,
  );
  const max = Math.max(1, ...counts);
  return (
    <ul className="flex flex-col gap-1 text-sm" aria-label="Score distribution">
      {DIST_BUCKETS.map(([lo, hi], i) => (
        <li key={lo} className="flex items-center gap-2">
          <span className="w-20 text-white/60 tabular-nums">
            {lo}–{hi}
          </span>
          <div className="h-3 flex-1 overflow-hidden rounded-full bg-white/10" aria-hidden="true">
            <div
              className="h-full rounded-full"
              style={{
                width: `${(counts[i]! / max) * 100}%`,
                backgroundColor: TIER_COLOR[tierForTotal(hi)],
              }}
            />
          </div>
          <span className="w-6 text-right tabular-nums">{counts[i]}</span>
        </li>
      ))}
    </ul>
  );
}

export function Stats() {
  const { data, error, loading, reload } = useApi<PlayerStats>('/me');

  useEffect(() => {
    track('stats_viewed');
  }, []);

  return (
    <Shell title="Your stats">
      {error && (
        <div className="rounded-2xl bg-red-500/10 p-4 text-sm text-red-200 ring-1 ring-red-500/30">
          <p className="mb-2">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="min-h-11 rounded-lg bg-white/10 px-4 font-semibold"
          >
            Retry
          </button>
        </div>
      )}
      {loading && !data && (
        <div className="grid grid-cols-2 gap-2" aria-hidden="true">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="skeleton h-20 rounded-2xl" />
          ))}
        </div>
      )}
      {data && (
        <div className="flex flex-col gap-4">
          <dl className="grid grid-cols-2 gap-2 text-sm">
            {[
              ['Current streak', `🔥 ${data.currentStreak}`],
              ['Best streak', `${data.bestStreak}`],
              ['Games played', `${data.gamesPlayed}`],
              ['Average score', data.avgScore === null ? '—' : `${data.avgScore}`],
            ].map(([k, v]) => (
              <div key={k} className="rounded-2xl bg-white/5 p-3">
                <dt className="text-white/50">{k}</dt>
                <dd className="text-2xl font-bold tabular-nums">{v}</dd>
              </div>
            ))}
          </dl>

          <section className="rounded-2xl bg-white/5 p-3">
            <h2 className="mb-2 text-sm font-semibold text-white/70">Last 7 days</h2>
            <WeekChart results={data.results} />
          </section>

          <section className="rounded-2xl bg-white/5 p-3">
            <h2 className="mb-2 text-sm font-semibold text-white/70">
              Score distribution (last 30 games)
            </h2>
            {data.results.length === 0 ? (
              <p className="text-sm text-white/60">
                Play a round to see your distribution.{' '}
                <Link to="/" className="text-accent underline underline-offset-2">
                  Play today’s round →
                </Link>
              </p>
            ) : (
              <Distribution results={data.results} />
            )}
          </section>

          {data.results.length > 0 && (
            <section className="rounded-2xl bg-white/5 p-3">
              <h2 className="mb-2 text-sm font-semibold text-white/70">History</h2>
              <ol className="divide-y divide-white/5 text-sm">
                {data.results.map((r) => (
                  <li key={r.date} className="flex items-center gap-3 py-2">
                    <span className="w-24 text-white/60">{prettyDate(r.date)}</span>
                    <span className="flex-1 tracking-wide">{emojiRow(r.bases)}</span>
                    <span
                      className="font-bold tabular-nums"
                      style={{ color: TIER_COLOR[tierForTotal(r.total)] }}
                    >
                      {r.total}
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          )}

          <p className="text-sm text-white/60">
            Playing as{' '}
            <strong className="text-white">{data.displayName || 'an anonymous fan'}</strong>. Change
            your name, sync another device or delete your data in Settings.{' '}
            <Link to="/practice" className="text-accent underline underline-offset-2">
              Missed a day? Replay it in Practice.
            </Link>
          </p>
        </div>
      )}
    </Shell>
  );
}
